import React from 'react';
import { Box, styled, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { FieldValues } from 'react-hook-form';
import type { EditableColumnDef, SortState } from './types';

const StyledHeaderRow = styled(TableRow)<{ $rowHeight?: string }>(({ theme, $rowHeight }) => ({
    height: $rowHeight || '48px',
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: theme.palette?.semantic?.bg?.raised,
    '& .MuiTableCell-root': {
        borderTop: `1px solid ${theme.palette.semantic.bd.subtle}`,
        borderBottom: `none`,
        borderRight: `1px solid ${theme.palette.semantic.bd.subtle}`,
        fontFamily: 'TT Commons Pro',
        fontSize: 'var(--Font-Size-text-sm, 14px)',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'var(--Font-Line-height-leading-4, 19px)',
        color: theme.palette.semantic.text.default
    },
    '& .MuiTableCell-root:first-of-type': {
        borderLeft: `1px solid ${theme.palette.semantic.bd.subtle}`,
        borderTopLeftRadius: '8px'
    },
    '& .MuiTableCell-root:last-of-type': {
        borderRight: `1px solid ${theme.palette.semantic.bd.subtle}`,
        borderTopRightRadius: '8px'
    }
}));

interface EditableTableHeaderProps<T extends FieldValues> {
    columns: EditableColumnDef<T>[];
    sort: SortState | null;
    // eslint-disable-next-line no-unused-vars
    onSortChange: (sort: SortState | null) => void;
    sortDisabled: boolean;
    rowHeight?: string;
    actionsColumnLabel?: string;
    actionsColumnWidth?: string;
    showActions: boolean;
}

export const EditableTableHeader = <T extends FieldValues>({
    columns,
    sort,
    onSortChange,
    sortDisabled,
    rowHeight,
    actionsColumnLabel = '',
    actionsColumnWidth = '120px',
    showActions
}: EditableTableHeaderProps<T>) => {
    const handleSort = (key: string) => {
        if (sortDisabled) return;
        if (sort?.key === key) {
            onSortChange(sort.direction === 'asc' ? { key, direction: 'desc' } : null);
        } else {
            onSortChange({ key, direction: 'asc' });
        }
    };

    return (
        <TableHead>
            <StyledHeaderRow $rowHeight={rowHeight}>
                {columns.map((column) => {
                    const isActive = sort?.key === column.key;
                    const labelContent = column.sortable ? (
                        <TableSortLabel
                            active={isActive}
                            direction={isActive ? sort?.direction : 'asc'}
                            onClick={() => handleSort(column.key)}
                            disabled={sortDisabled}
                        >
                            {column.label}
                        </TableSortLabel>
                    ) : (
                        column.label
                    );
                    return (
                        <TableCell
                            key={column.key}
                            align={column.align ?? 'left'}
                            sx={{ width: column.width, verticalAlign: 'middle' }}
                            sortDirection={isActive ? sort?.direction : false}
                        >
                            {column.labelInfo ? (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    {labelContent}
                                    {column.labelInfo}
                                </Box>
                            ) : (
                                labelContent
                            )}
                        </TableCell>
                    );
                })}
                {showActions && (
                    <TableCell align="left" sx={{ width: actionsColumnWidth, verticalAlign: 'middle' }}>
                        {actionsColumnLabel}
                    </TableCell>
                )}
            </StyledHeaderRow>
        </TableHead>
    );
};
